// The order-line edit's form logic, as plain functions — no React.
//
// The modal (`edit-order-lines-modal.tsx`) holds one text draft per editable
// cell; these turn the drafts into the PATCH body and the live preview. Kept
// out of the component so the rules (what counts as a change, what is sent,
// what the totals will become) are testable without rendering.

import {
  ORDER_LINE_EDITABLE_FIELDS,
  type OrderLineEditableField,
  type OrderLinePatch,
} from '@commissions/auth/order-lines';
import type { EditableOrderLine } from '@/app-kit/use-orders';
import type { AccessMap } from './access-map';

export { ORDER_LINE_EDITABLE_FIELDS, type OrderLineEditableField, type OrderLinePatch };

/** Column labels, matching what the invoice table shows for the same fields. */
export const ORDER_LINE_FIELD_LABELS: Record<OrderLineEditableField, string> = {
  quantity:                   'Qty',
  business_volume_each:       'PPV Each',
  commissionable_volume_each: 'CV Each',
};

/** One line's drafts, as typed. */
export type LineDraft = Record<OrderLineEditableField, string>;
/** Drafts for every line, keyed by `order_line`. */
export type LineDrafts = Record<number, LineDraft>;

/** The fields this caller may edit. An ABSENT access entry means "no restriction". */
export function editableLineFields(access: AccessMap | undefined): OrderLineEditableField[] {
  return ORDER_LINE_EDITABLE_FIELDS.filter((f) => access?.[f]?.editable !== false);
}

/** The drafts a freshly-opened modal starts from: each line's stored values. */
export function initialDrafts(lines: readonly EditableOrderLine[]): LineDrafts {
  const drafts: LineDrafts = {};
  for (const l of lines) {
    drafts[l.order_line] = Object.fromEntries(
      ORDER_LINE_EDITABLE_FIELDS.map((f) => [f, trimZeros(String(l[f] ?? ''))]),
    ) as LineDraft;
  }
  return drafts;
}

/** `'2.0000'` → `'2'`, `'12.5000'` → `'12.5'`: the API's numeric(19,4) form, as a person would type it. */
function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/** A draft as a number, or null when it is not one. Blank is not zero. */
export function parseDraft(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export type LinesPatchResult = {
  /** Only lines with a changed field, and only the changed fields. */
  lines:   OrderLinePatch[];
  /** Cells whose draft is not a number — the save is blocked while any exist. */
  invalid: Array<{ order_line: number; field: OrderLineEditableField }>;
};

/**
 * The PATCH body's `lines`. A field is sent only if the caller may edit it AND
 * its draft differs numerically from the stored value (so `2` vs `2.0000` is
 * not a change). Read-only fields are never sent, whatever their draft holds.
 */
export function buildLinesPatch(
  lines: readonly EditableOrderLine[],
  drafts: LineDrafts,
  editable: readonly OrderLineEditableField[],
): LinesPatchResult {
  const out: OrderLinePatch[] = [];
  const invalid: LinesPatchResult['invalid'] = [];
  for (const l of lines) {
    const draft = drafts[l.order_line];
    if (!draft) continue;
    const patch: OrderLinePatch = { order_line: l.order_line };
    let changed = false;
    for (const f of editable) {
      const n = parseDraft(draft[f]);
      if (n === null) { invalid.push({ order_line: l.order_line, field: f }); continue; }
      if (n !== Number(l[f])) { patch[f] = n; changed = true; }
    }
    if (changed) out.push(patch);
  }
  return { lines: out, invalid };
}

export type LinePreview = { business_volume: number | null; commissionable_volume: number | null };

/**
 * What the line's extended volumes will become.
 *
 * An UNCHANGED line keeps its stored volumes — the server only recomputes the
 * lines it writes, and a stored volume need not equal quantity × each (hand-
 * adjusted lines exist). A changed line becomes quantity × each, rounded to
 * the column's 4 places. Display only: the server does the real arithmetic in
 * exact numeric.
 */
export function previewLine(line: EditableOrderLine, draft: LineDraft | undefined): LinePreview {
  const stored = {
    business_volume:       Number(line.business_volume),
    commissionable_volume: Number(line.commissionable_volume),
  };
  if (!draft) return stored;
  const values = ORDER_LINE_EDITABLE_FIELDS.map((f) => parseDraft(draft[f]));
  const unchanged = ORDER_LINE_EDITABLE_FIELDS.every((f, i) => values[i] === Number(line[f]));
  if (unchanged) return stored;
  const [q, bv, cv] = values as [number | null, number | null, number | null];
  return {
    business_volume:       q === null || bv === null ? null : round4(q * bv),
    commissionable_volume: q === null || cv === null ? null : round4(q * cv),
  };
}

/**
 * What the order's PPV/CV totals will become: the SUM of every line's
 * volume, which is how the server recomputes them. Null while any line's
 * preview is incomplete.
 */
export function previewTotals(lines: readonly EditableOrderLine[], drafts: LineDrafts): LinePreview {
  let bv: number | null = 0;
  let cv: number | null = 0;
  for (const l of lines) {
    const p = previewLine(l, drafts[l.order_line]);
    bv = bv === null || p.business_volume === null ? null : bv + p.business_volume;
    cv = cv === null || p.commissionable_volume === null ? null : cv + p.commissionable_volume;
  }
  return {
    business_volume:       bv === null ? null : round4(bv),
    commissionable_volume: cv === null ? null : round4(cv),
  };
}

function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}
